import React from 'react';
import { IconButton } from '@smart-tv/components/icon-button/icon-button';
import { ArrowDownIcon } from '@smart-tv/components/icons/arrow-down';
import styles from './content-slider.module.scss';
import clsx from 'clsx';
import { testIds } from '@smart-tv/e2e/shared/variables';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';

interface Props {
  scrollingRef?: React.RefObject<HTMLDivElement>;
  onShowAll?: () => void;
  showAllOnly?: boolean;
  arrowsOnly?: boolean;
}

export const ContentSliderNavigation: React.FC<Props> = ({
  scrollingRef,
  onShowAll,
  showAllOnly = false,
  arrowsOnly = false
}) => {
  const [hasOverflow, setHasOverflow] = React.useState(false);
  const [prevDisabled, setPrevDisabled] = React.useState(true);
  const [nextDisabled, setNextDisabled] = React.useState(false);

  const updateButtonStates = React.useCallback(() => {
    const el = scrollingRef?.current;
    if (!el) return;

    const canScroll = el.scrollWidth > el.clientWidth + 1;
    setHasOverflow(canScroll);
    if (!canScroll) {
      setPrevDisabled(true);
      setNextDisabled(true);
      return;
    }

    setPrevDisabled(el.scrollLeft <= 0);
    setNextDisabled(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, [scrollingRef]);

  React.useEffect(() => {
    const el = scrollingRef?.current;
    if (!el) return;
    updateButtonStates();
    el.addEventListener('scroll', updateButtonStates);
    window.addEventListener('resize', updateButtonStates);
    return () => {
      el.removeEventListener('scroll', updateButtonStates);
      window.removeEventListener('resize', updateButtonStates);
    };
  }, [scrollingRef, updateButtonStates]);

  const handlePrev = React.useCallback(() => {
    const el = scrollingRef?.current;
    if (!el) return;
    el.scrollBy({ left: -el.clientWidth, behavior: 'smooth' });
  }, [scrollingRef]);

  const handleNext = React.useCallback(() => {
    const el = scrollingRef?.current;
    if (!el) return;
    el.scrollBy({ left: el.clientWidth, behavior: 'smooth' });
  }, [scrollingRef]);

  if (!IS_BROWSER) return null;

  return (
    <div
      className={clsx(styles['navigation'], {
        [styles['navigation--hidden']]: !showAllOnly && !hasOverflow,
        [styles['navigation--overlay']]: arrowsOnly
      })}
    >
      {onShowAll && !arrowsOnly && (
        <button
          className={styles['navigation__show-all']}
          onClick={onShowAll}
          data-testid={testIds.contentSlider.actions.showAll}
        >
          Vis alle
        </button>
      )}
      {!showAllOnly && (
        <>
          <IconButton
            className={clsx(
              styles['navigation__arrow'],
              styles['navigation__arrow--prev'],
              prevDisabled && styles['navigation__arrow--inactive']
            )}
            size="lg"
            background={true}
            onClick={handlePrev}
            disabled={prevDisabled}
            aria-label="Forrige episoder"
            data-testid={testIds.contentSlider.actions.prev}
            icon={<ArrowDownIcon />}
          />
          <IconButton
            className={clsx(
              styles['navigation__arrow'],
              styles['navigation__arrow--next'],
              nextDisabled && styles['navigation__arrow--inactive']
            )}
            size="lg"
            background={true}
            onClick={handleNext}
            disabled={nextDisabled}
            aria-label="Næste episoder"
            data-testid={testIds.contentSlider.actions.next}
            icon={<ArrowDownIcon />}
          />
        </>
      )}
    </div>
  );
};

export default ContentSliderNavigation;
